/* eslint-disable default-case */
/* eslint-disable import/prefer-default-export */
/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */


import Call from './call';

const util = require('./util.js');

const PACKET_LOST_RANGE = [2, 6];
const RTT_RANGE = [200, 500];
const BANDWIDTH_RANGE_VIDEO = [200, 500];
const BANDWIDTH_RANGE_AUDIO = [10, 30];
const INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE= [250, 500, 800];
const JITTER_RANGE = [0, 30];

const GOOD = 'good';
const MODERATE = 'moderate';
const POOR = 'poor';

function DataChannelThroughputTest(test, testDuration) {
  this.test = test;
  this.testDurationSeconds = testDuration * 1.0;
  this.startTime = null;
  this.sentPayloadBytes = 0;
  this.receivedPayloadBytes = 0;
  this.stopSending = false;
  this.samplePacket = '';

  for (let i = 0; i !== 1024; ++i) {
    this.samplePacket += 'h';
  }

  this.maxNumberOfPacketsToSend = 1;
  this.bytesToKeepBuffered = 1024 * this.maxNumberOfPacketsToSend;
  this.lastBitrateMeasureTime = null;
  this.lastReceivedPayloadBytes = 0;

  this.call = null;
  this.senderChannel = null;
  this.receiveChannel = null;
}

DataChannelThroughputTest.prototype = {
  run() {
    if (this.test.server === 'TURN') {
      Call.asyncCreateTurnConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    } else {
      Call.asyncCreateStunConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    }
  },

  start(config) {
    this.call = new Call(config, this.test);
    this.call.setIceCandidateFilter(Call.isRelay);
    this.senderChannel = this.call.pc1.createDataChannel(null);
    this.senderChannel.addEventListener('open', this.sendingStep.bind(this));

    this.call.pc2.addEventListener(
      'datachannel',
      this.onReceiverChannel.bind(this),
    );

    this.call.establishConnection();
    this.timeout = setTimeout(this.hangup.bind(this, 'Timed out'), 30000); //patch
  },
   hangup(errorMessage) {
    //patch
    if (errorMessage) {
      if (errorMessage === 'Timed out') {
        this.test.reportError('maximum timeout');
      } else {
        this.test.reportError(errorMessage);
      }
    }
    clearTimeout(this.timeout);
    this.call.close();
    this.test.done();
  },
  onReceiverChannel(event) {
    this.receiveChannel = event.channel;
    this.receiveChannel.addEventListener(
      'message',
      this.onMessageReceived.bind(this),
    );
  },

  sendingStep() {
    clearTimeout(this.timeout); //patch
    const now = new Date();
    if (!this.startTime) {
      this.startTime = now;
      this.lastBitrateMeasureTime = now;
    }

    for (let i = 0; i !== this.maxNumberOfPacketsToSend; ++i) {
      if (this.senderChannel.bufferedAmount >= this.bytesToKeepBuffered) {
        break;
      }
      this.sentPayloadBytes += this.samplePacket.length;
      this.senderChannel.send(this.samplePacket);
    }

    if (now - this.startTime >= 1000 * this.testDurationSeconds) {
      this.test.setProgress(100);
      this.stopSending = true;
    } else {
      this.test.setProgress((now - this.startTime) /
          (10 * this.testDurationSeconds));
      setTimeout(this.sendingStep.bind(this), 1);
    }
  },

  onMessageReceived(event) {
    this.receivedPayloadBytes += event.data.length;
    const now = new Date();
    if (now - this.lastBitrateMeasureTime >= 1000) {
      let bitrate = (this.receivedPayloadBytes -
          this.lastReceivedPayloadBytes) / (now - this.lastBitrateMeasureTime);
      bitrate = Math.round(bitrate * 1000 * 8) / 1000;
      this.test.reportSuccess(`Transmitting at ${bitrate} kbps.`);
      this.lastReceivedPayloadBytes = this.receivedPayloadBytes;
      this.lastBitrateMeasureTime = now;
    }
    if (this.stopSending &&
        this.sentPayloadBytes === this.receivedPayloadBytes) {
      this.call.close();
      this.call = null;

      const elapsedTime = Math.round((now - this.startTime) * 10) / 10000.0;
      const receivedKBits = (this.receivedPayloadBytes * 8) / 1000;
      this.test.reportSuccess(`Total transmitted: ${receivedKBits
          } kilo-bits in ${elapsedTime} seconds.`);
      this.test.done();
    }
  },
};

function VideoBandwidthTest(test, deviceId, testDuration) {
  this.test = test;
  this.maxVideoBitrateKbps = 2000;
  this.durationMs = testDuration  * 1000;
  this.statStepMs = 100;
  this.bweStats = new util.StatisticsAggregate(0.75 * this.maxVideoBitrateKbps *
      1000);
  this.rttStats = new util.StatisticsAggregate();
  this.packetsLost = -1;
  this.nackCount = -1;
  this.pliCount = -1;
  this.qpSum = -1;
  this.packetsSent = 0;
  this.packetsReceived = 0;
  this.framesEncoded = -1;
  this.framesDecoded = -1;
  this.videoBytesSent = -1;
  this.videoBytesRecieved = -1;
  this.framesSent = -1;
  this.bitrateMean = -1;
  this.videoStats = [];
  this.startTime = null;
  this.call = null;
  this.deviceId = deviceId;
  this.bytesSent = 0;
  this.constraints = {
    audio: false,
    video: {
      optional: [
        { minWidth: 320 },
        { minHeight: 240 },
      ],
    },
  };
}

VideoBandwidthTest.prototype = {
  run() {
    if (this.test.server === 'TURN') {
      Call.asyncCreateTurnConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    } else {
      Call.asyncCreateStunConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    }
  },

  start(config) {
    this.call = new Call(config, this.test);
    this.call.setIceCandidateFilter(Call.isRelay);
    // FEC makes it hard to study bandwidth estimation since there seems to be
    // a spike when it is enabled and disabled. Disable it for now. FEC issue
    // tracked on: https://code.google.com/p/webrtc/issues/detail?id=3050
    this.call.disableVideoFec();
    this.call.constrainVideoBitrate(this.maxVideoBitrateKbps);
    util.appendSourceId(this.deviceId, 'video', this.constraints);
    util.doGetUserMedia(this.constraints, this.gotStream.bind(this), this.onFail.bind(this));
  },

  onFail(error) {
    this.test.reportError(`getUserMedia failed with error: ${error}`);
    this.test.done();
  },

  gotStream(stream) {
    this.call.pc1.addStream(stream);
    this.call.establishConnection();
    this.startTime = new Date();
    this.localStream = stream.getVideoTracks()[0];
    setTimeout(this.gatherStats.bind(this), this.statStepMs);
  },

  gatherStats() {
    const now = new Date();
    if (now - this.startTime > this.durationMs) {
      this.test.setProgress(100);
      this.hangup();
      return;
    } else if (!this.call.statsGatheringRunning) {
      this.call.gatherStats(
        this.call.pc1, this.call.pc2, this.localStream,
        this.gotStats.bind(this),
      );
    }
    this.test.setProgress((now - this.startTime) * 100 / this.durationMs);
    setTimeout(this.gatherStats.bind(this), this.statStepMs);
  },

  gotStats(response, time, response2, time2) {
    // TODO: Remove browser specific stats gathering hack once adapter.js or
    // browsers converge on a standard.
    if (adapter.browserDetails.browser === 'chrome') {
      for (const i in response) {
        if (typeof response[i].connection !== 'undefined') {
          this.bweStats.add(
            response[i].connection.timestamp,
            parseInt(response[i].connection.availableOutgoingBitrate),
          );
          this.rttStats.add(
            response[i].connection.timestamp,
            parseInt(response[i].connection.currentRoundTripTime * 1000),
          );
          // Grab the last stats.
          this.videoStats[0] = response[i].video.local.frameWidth;
          this.videoStats[1] = response[i].video.local.frameHeight;
          this.nackCount = response[i].video.local.nackCount;
          this.packetsLost = response2[i].video.remote.packetsLost;
          this.qpSum = response2[i].video.remote.qpSum;
          this.pliCount = response[i].video.local.pliCount;
          this.packetsSent = response[i].video.local.packetsSent;
          this.packetsReceived = response2[i].video.remote.packetsReceived;
          this.framesEncoded = response[i].video.local.framesEncoded;
          this.videoBytesSent = response[i].video.local.bytesSent;
          this.videoBytesRecieved = response2[i].video.remote.bytesReceived;
          this.framesDecoded = response2[i].video.remote.framesDecoded;
        }
      }
    } else if (adapter.browserDetails.browser === 'firefox') {
      for (const j in response) {
        let rtt = response[j].video.remote.roundTripTime * 1000;
        if(rtt) {
          this.rttStats.add(
          response[j].video.remote.timestamp,
          parseInt(rtt),
          );
        }
        this.bytesSent += response[j].connection.bytesSent;
        this.packetsSent = response[j].video.local.packetsSent;
        this.packetsLost = response2[j].video.remote.packetsLost;
        this.packetsReceived = response2[j].video.remote.packetsReceived;
        this.qpSum = response2[j].video.remote.qpSum;
        this.bitrateMean = response[j].video.local.bitrateMean;
        this.videoBytesSent = response[j].video.local.bytesSent;
        this.videoBytesRecieved = response2[j].video.remote.bytesReceived;
      }
    } else {
      this.test.reportError('Only Firefox and Chrome getStats implementations' +
        ' are supported.');
    }
    this.completed();
  },

  hangup() {
    this.call.pc1.getLocalStreams()[0].getTracks().forEach((track) => {
      track.stop();
    });
    this.call.close();
    this.call = null;
  },

  completed() {
    // TODO: Remove browser specific stats gathering hack once adapter.js or
    // browsers converge on a standard.
    if (adapter.browserDetails.browser === 'chrome') {
      const now = new Date();
      const durationS = (now - this.startTime) / 1000;
      // Checking if greater than 2 because Chrome sometimes reports 2x2 when
      // a camera starts but fails to deliver frames.
      if (this.videoStats[0] < 2 && this.videoStats[1] < 2) {
        this.test.reportError(`Camera failure: ${this.videoStats[0]}x${
            this.videoStats[1]}. Cannot test bandwidth without a working ` +
            ' camera.');
      } else {
        this.test.reportSuccess(`Video resolution: ${this.videoStats[0]
            }x${this.videoStats[1]}`);
        let sendBWEstimateAvg = Math.round(this.bweStats.getAverage() / 1000);
        let sendBWEstimateAvgState = null;
        if (sendBWEstimateAvg < BANDWIDTH_RANGE_VIDEO[0]) {
          sendBWEstimateAvgState = POOR;
        } else if (sendBWEstimateAvg < BANDWIDTH_RANGE_VIDEO[1]) {
          sendBWEstimateAvgState = MODERATE;
        } else {
          sendBWEstimateAvgState = GOOD;
        }
        let possible_no_incoming_videos = 0;
        if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[0]) {
          possible_no_incoming_videos = 1;
        } else if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[1]) {
          possible_no_incoming_videos = 2;
        } else if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[2]) {
          possible_no_incoming_videos = 4;
        } else {
          possible_no_incoming_videos = 5;
        }
        this.test.reportByState(`Average Video bandwidth (Send): ${sendBWEstimateAvg} kbps`, sendBWEstimateAvgState);
        if(true === this.test.bitrate_only) {
          this.test.fillBandwidth(this.bweStats.getAverage());
        }

        if(false === this.test.bitrate_only) {
          this.test.reportInfo(`Recommended no. of incoming videos :${possible_no_incoming_videos}`);
          this.test.reportInfo(`Send bandwidth estimate max: ${
              this.bweStats.getMax() / 1000} kbps`);
          this.test.reportInfo(`Send bandwidth ramp-up time: ${
              this.bweStats.getRampUpTime()} ms`);
          this.test.reportInfo(`Packets sent: ${this.packetsSent}`);
          this.test.reportInfo(`Packets received: ${this.packetsReceived}`);
          this.test.reportInfo(`NACK count: ${this.nackCount}`);
          this.test.reportInfo(`Picture loss indications: ${this.pliCount}`);
          this.test.reportInfo(`Quality predictor sum: ${this.qpSum}`);
          this.test.reportInfo(`Frames encoded: ${this.framesEncoded}`);
          this.test.reportInfo(`Frames decoded: ${this.framesDecoded}`);
        }
        let pktLoss = null;
        let pktLostPercentage = null;
        if(this.packetsSent > 0) {
          pktLostPercentage = ((this.packetsSent - this.packetsReceived) / this.packetsSent) * 100;
      
          if (pktLostPercentage < PACKET_LOST_RANGE[0]) {
            pktLoss = GOOD;
          } else if (pktLostPercentage < PACKET_LOST_RANGE[1]) {
            pktLoss = MODERATE;
          } else {
            pktLoss = POOR;
          }
      
          this.test.reportByState(`Packets lost: ${pktLostPercentage.toFixed(2)}%`, pktLoss);
          if(true === this.test.bitrate_only) {
            this.test.fillPacketsLost(pktLostPercentage.toFixed(2));
          }
        }
        let bandwidthSend = ((this.videoBytesSent * 8) / (durationS * 1000)).toFixed(2);
        let bandwidthReceived = ((this.videoBytesRecieved * 8) / (durationS * 1000)).toFixed(2);

        this.test.reportInfo(`Video Bitrate (Send) :${bandwidthSend} kbps`);
        this.test.reportInfo(`Video Bitrate (Receive) :${bandwidthReceived} kbps`);
      }
    } else if (adapter.browserDetails.browser === 'firefox') {
      this.test.reportInfo(`Bitrate Mean (Send): ${parseInt(this.bitrateMean) / 1000} kbps`);
      const now = new Date();
      const durationS = (now - this.startTime) / 1000;
      let sendBWEstimateAvg = ((this.bytesSent * 8) / (durationS * 1000)).toFixed(2);
      let sendBWEstimateAvgState = null;
      if (sendBWEstimateAvg < BANDWIDTH_RANGE_VIDEO[0]) {
        sendBWEstimateAvgState = POOR;
      } else if (sendBWEstimateAvg < BANDWIDTH_RANGE_VIDEO[1]) {
        sendBWEstimateAvgState = MODERATE;
      } else {
        sendBWEstimateAvgState = GOOD;
      }
      let possible_no_incoming_videos = 0;
      if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[0]) {
        possible_no_incoming_videos = 1;
      } else if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[1]) {
        possible_no_incoming_videos = 2;
      } else if(sendBWEstimateAvg < INCOMING_VIDEO_COUNT_BANDWIDTH_RANGE[2]) {
        possible_no_incoming_videos = 4;
      } else {
        possible_no_incoming_videos = 5;
      }
      this.test.reportByState(`Average Video bandwidth (Send): ${sendBWEstimateAvg} kbps`, sendBWEstimateAvgState);
      if(true === this.test.bitrate_only) {
        this.test.fillBandwidth(sendBWEstimateAvg * 1000);
      }
      if(false === this.test.bitrate_only) {
        this.test.reportInfo(`Packets sent: ${this.packetsSent}`);
        this.test.reportInfo(`Packets received: ${this.packetsReceived}`);
      }
      let pktLoss = null;
      let pktLostPercentage = null;
      if(this.packetsSent > 0) {
        pktLostPercentage = ((this.packetsSent - this.packetsReceived) / this.packetsSent) * 100;
    
        if (pktLostPercentage < PACKET_LOST_RANGE[0]) {
          pktLoss = GOOD;
        } else if (pktLostPercentage < PACKET_LOST_RANGE[1]) {
          pktLoss = MODERATE;
        } else {
          pktLoss = POOR;
        }
    
        this.test.reportByState(`Packets lost: ${pktLostPercentage.toFixed(2)}%`, pktLoss);
        if(true === this.test.bitrate_only) {
          this.test.fillPacketsLost(pktLostPercentage.toFixed(2));
        }
      }
      let bandwidthSend = ((this.videoBytesSent * 8) / (durationS * 1000)).toFixed(2);
      let bandwidthReceived = ((this.videoBytesRecieved * 8) / (durationS * 1000)).toFixed(2);

      this.test.reportInfo(`Video Bitrate (Send) :${bandwidthSend} kbps`);
      this.test.reportInfo(`Video Bitrate (Receive) :${bandwidthReceived} kbps`);
    }
    let rttAverage = this.rttStats.getAverage();
    let rttValue = null;

    if (rttAverage < RTT_RANGE[0]) {
      rttValue = GOOD;
    } else if (rttAverage < RTT_RANGE[1]) {
      rttValue = MODERATE;
    } else {
      rttValue = POOR;
    }
    this.test.reportByState(`RTT average: ${rttAverage} ms`, rttValue);
    if(false === this.test.bitrate_only) {
      this.test.reportInfo(`RTT max: ${this.rttStats.getMax()} ms`);
    }
    this.test.done();
  },
};

function WiFiPeriodicScanTest(test, candidateFilter) {
  this.test = test;
  this.candidateFilter = candidateFilter;
  this.testDurationMs = 5 * 60 * 1000;
  this.sendIntervalMs = 100;
  this.delays = [];
  this.recvTimeStamps = [];
  this.running = false;
  this.call = null;
  this.senderChannel = null;
  this.receiveChannel = null;
}

WiFiPeriodicScanTest.prototype = {
  run() {
    if (this.test.server === 'TURN') {
      Call.asyncCreateTurnConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    } else {
      Call.asyncCreateStunConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
    );
    }
  },

  start(config) {
    this.filterConfig(config);
    this.running = true;
    this.call = new Call(config, this.test);
    //this.chart = this.test.createLineChart();
    this.call.setIceCandidateFilter(this.candidateFilter);

    this.senderChannel = this.call.pc1.createDataChannel({
      ordered: false,
      maxRetransmits: 0,
    });
    this.senderChannel.addEventListener('open', this.send.bind(this));
    this.call.pc2.addEventListener(
      'datachannel',
      this.onReceiverChannel.bind(this),
    );
    this.call.establishConnection();

    util.setTimeoutWithProgressBar(
    this.finishTest.bind(this),
      this.testDurationMs,
    );
  },

  filterConfig(config) {
    const newIceServers = [];
    const iceServer = config.iceServers[0];
    const new_urls = [];
    const transport = 'transport=tcp';
    const uri = (iceServer.urls.startsWith('turns')) ? `${iceServer.urls}?${transport}` : iceServer.urls;
    new_urls.push(uri);
    iceServer.urls = new_urls;
    newIceServers.push(iceServer);
    config.iceServers = newIceServers;
  },
  onReceiverChannel(event) {
    this.receiveChannel = event.channel;
    this.receiveChannel.addEventListener('message', this.receive.bind(this));
  },

  send() {
    if (!this.running) {
      return;
    }
    this.senderChannel.send(`${Date.now()}`);
    setTimeout(this.send.bind(this), this.sendIntervalMs);
  },

  receive(event) {
    if (!this.running) {
      return;
    }
    const sendTime = parseInt(event.data);
    const delay = Date.now() - sendTime;
    this.recvTimeStamps.push(sendTime);
    this.delays.push(delay);
    this.test.fillData(sendTime + delay, delay);
    //console.log('timestamp: ' + sendTime + delay + ' value: '+ delay);
    //this.chart.addDatapoint(sendTime + delay, delay);
  },

  finishTest() {
    // report.traceEventInstant('periodic-delay', {
    // delays: this.delays,
    //       recvTimeStamps: this.recvTimeStamps,
    // });
    this.running = false;
    this.call.close();
    this.call = null;
    //this.chart.parentElement.removeChild(this.chart);

    //let avg = util.arrayAverage(this.delays);
    let max = util.arrayMax(this.delays);
    let min = util.arrayMin(this.delays);
    // this.test.reportInfo('Average delay: ' + avg + ' ms.');
    // this.test.reportInfo('Min delay: ' + min + ' ms.');
    // this.test.reportInfo('Max delay: ' + max + ' ms.');

    if (this.delays.length < (0.8 * this.testDurationMs) / this.sendIntervalMs) {
      this.test.reportError('Not enough samples gathered. Keep the page on ' +
          ' the foreground while the test is running.');
    } else {
      this.test.reportSuccess(`Collected ${this.delays.length
          } delay samples.`);
    }

    if (max > (min + 100) * 2) {
      this.test.reportError('There is a big difference between the min and ' +
          'max delay of packets. Your network appears unstable.' + 'min='+min + 'max='+max);
    }
    this.test.done();
  },
};

function AudioBandwidthTest(test, deviceId, testDuration) {
  this.test = test;
  this.maxAudioBitrateKbps = 2000;
  this.durationMs = testDuration * 1000;
  this.statStepMs = 100;
  this.bweStats = new util.StatisticsAggregate(0.75 * this.maxAudioBitrateKbps *
      1000);
  this.rttStats = new util.StatisticsAggregate();
  this.packetsLost = -1;
  this.packetsSent = -1;
  this.packetsReceived = -1;
  this.audioBytesSent = -1;
  this.audioBytesRecieved = -1;
  this.startTime = null;
  this.jitter = 0;
  this.call = null;
  this.bytesSent = 0;
  this.deviceId = deviceId;
  this.constraints = {
    audio: true,
  };
}

AudioBandwidthTest.prototype = {
  run() {
    if (this.test.server === 'TURN') {
      Call.asyncCreateTurnConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    } else {
      Call.asyncCreateStunConfig(
        this.start.bind(this),
        this.test.reportFatal.bind(this.test), this.test.region,
      );
    }
  },

  start(config) {
    this.call = new Call(config, this.test);
    this.call.setIceCandidateFilter(Call.isRelay);
    // FEC makes it hard to study bandwidth estimation since there seems to be
    // a spike when it is enabled and disabled. Disable it for now. FEC issue
    // tracked on: https://code.google.com/p/webrtc/issues/detail?id=3050
    //this.call.disableVideoFec();
    //this.call.constrainVideoBitrate(this.maxVideoBitrateKbps);
    util.appendSourceId(this.deviceId, 'audio', this.constraints);
    util.doGetUserMedia(this.constraints, this.gotStream.bind(this), this.onFail.bind(this));
  },

  onFail(error) {
    this.test.reportError(`getUserMedia failed with error: ${error}`);
    this.test.done();
  },

  gotStream(stream) {
    this.call.pc1.addStream(stream);
    this.call.establishConnection();
    this.startTime = new Date();
    this.localStream = stream.getAudioTracks()[0];
    setTimeout(this.gatherStats.bind(this), this.statStepMs);
  },

  gatherStats() {
    const now = new Date();
    if (now - this.startTime > this.durationMs) {
      this.test.setProgress(100);
      this.hangup();
      return;
    } else if (!this.call.statsGatheringRunning) {
      this.call.gatherStats(
        this.call.pc1, this.call.pc2, this.localStream,
        this.gotStats.bind(this),
      );
    }
    this.test.setProgress((now - this.startTime) * 100 / this.durationMs);
    setTimeout(this.gatherStats.bind(this), this.statStepMs);
  },

  gotStats(response, time, response2, time2) {
    // TODO: Remove browser specific stats gathering hack once adapter.js or
    // browsers converge on a standard.
    if (adapter.browserDetails.browser === 'chrome') {
      for (const i in response) {
        if (typeof response[i].connection !== 'undefined') {
          this.bweStats.add(
            response[i].connection.timestamp,
            parseInt(response[i].connection.availableOutgoingBitrate),
          );
          this.rttStats.add(
            response[i].connection.timestamp,
            parseInt(response[i].connection.currentRoundTripTime * 1000),
          );
          // Grab the last stats.
          this.packetsLost = response2[i].audio.remote.packetsLost;
          this.packetsSent = response[i].audio.local.packetsSent;
          this.packetsReceived = response2[i].audio.remote.packetsReceived;
          this.audioBytesSent = response[i].audio.local.bytesSent;
          this.audioBytesRecieved = response2[i].audio.remote.bytesReceived;
          this.jitter = response2[i].audio.remote.jitter;
        }
      }
    } else if (adapter.browserDetails.browser === 'firefox') {
      for (const j in response) {
        let rtt = response[j].audio.remote.roundTripTime * 1000;
        if(rtt) {
          this.rttStats.add(
          response[j].audio.remote.timestamp,
          parseInt(rtt),
          );
        }
        this.bytesSent += response[j].connection.bytesSent;
        this.packetsSent = response[j].audio.local.packetsSent;
        this.packetsLost = response2[j].audio.remote.packetsLost;
        this.packetsReceived = response2[j].audio.remote.packetsReceived;
        this.jitter = response2[j].audio.remote.jitter;
        this.audioBytesSent = response[j].audio.local.bytesSent;
        this.audioBytesRecieved = response2[j].audio.remote.bytesReceived;
      }
    } else {
      this.test.reportError('Only Firefox and Chrome getStats implementations' +
        ' are supported.');
    }
    this.completed();
  },

  hangup() {
    this.call.pc1.getLocalStreams()[0].getTracks().forEach((track) => {
      track.stop();
    });
    this.call.close();
    this.call = null;
  },

  completed() {
    // TODO: Remove browser specific stats gathering hack once adapter.js or
    // browsers converge on a standard.
    if (adapter.browserDetails.browser === 'chrome') {
      const now = new Date();
      const durationS = (now - this.startTime) / 1000;
      {
        let sendBWEstimateAvg = Math.round(this.bweStats.getAverage() / 1000);
        let sendBWEstimateAvgState = null;
        if (sendBWEstimateAvg < BANDWIDTH_RANGE_AUDIO[0]) {
          sendBWEstimateAvgState = POOR;
        } else if (sendBWEstimateAvg < BANDWIDTH_RANGE_AUDIO[1]) {
          sendBWEstimateAvgState = MODERATE;
        } else {
          sendBWEstimateAvgState = GOOD;
        }
        this.test.reportByState(`Average Audio bandwidth (Send): ${sendBWEstimateAvg} kbps`, sendBWEstimateAvgState);
        if(true === this.test.bitrate_only) {
          this.test.fillBandwidth(this.bweStats.getAverage());
        }
        if(false == this.test.bitrate_only) {
          this.test.reportInfo(`Send bandwidth estimate max: ${
              this.bweStats.getMax() / 1000} kbps`);
          this.test.reportInfo(`Send bandwidth ramp-up time: ${
              this.bweStats.getRampUpTime()} ms`);
          this.test.reportInfo(`Packets sent: ${this.packetsSent}`);
          this.test.reportInfo(`Packets received: ${this.packetsReceived}`);
        }
        let pktLoss = null;
        let pktLostPercentage = null;
        if(this.packetsSent > 0) {
          pktLostPercentage = ((this.packetsSent - this.packetsReceived) / this.packetsSent) * 100;
          if (pktLostPercentage < PACKET_LOST_RANGE[0]) {
            pktLoss = GOOD;
          } else if (pktLostPercentage < PACKET_LOST_RANGE[1]) {
            pktLoss = MODERATE;
          } else {
            pktLoss = POOR;
          }
          this.test.reportByState(`Packets lost: ${pktLostPercentage.toFixed(2)}%`, pktLoss);
          if(true === this.test.bitrate_only) {
            this.test.fillPacketsLost(pktLostPercentage.toFixed(2));
          }
        }
        let bandwidthSend = ((this.audioBytesSent * 8) / (durationS * 1000)).toFixed(2);
        let bandwidthReceived = ((this.audioBytesRecieved * 8) / (durationS * 1000)).toFixed(2);

        this.test.reportInfo(`Audio Bitrate (Send) :${bandwidthSend} kbps`);
        this.test.reportInfo(`Audio Bitrate (Receive) :'${bandwidthReceived} 'kbps`);

        let jit = this.jitter * 1000;
        let jitter = null;
        if(jit > JITTER_RANGE[1] || jit <= JITTER_RANGE[0]) {
          jitter = POOR;
        } else {
          jitter = GOOD;
          this.test.reportByState(`Jitter: ${jit}`, jitter);
        }
      }
    } else if (adapter.browserDetails.browser === 'firefox') {
      const now = new Date();
      const durationS = (now - this.startTime) / 1000;
      // let sendBWEstimateAvg = Math.round(this.bweStats.getAverage() / 1000);
      // let sendBWEstimateAvgState = null;
      // if (sendBWEstimateAvg < BANDWIDTH_RANGE_AUDIO[0]) {
      //   sendBWEstimateAvgState = POOR;
      // } else if (sendBWEstimateAvg < BANDWIDTH_RANGE_AUDIO[1]) {
      //   sendBWEstimateAvgState = MODERATE;
      // } else {
      //   sendBWEstimateAvgState = GOOD;
      // }
      // this.test.reportByState(`Average Audio bandwidth (Send): ${sendBWEstimateAvg} kbps`, sendBWEstimateAvgState);
      // if(true === this.test.bitrate_only) {
      //   this.test.fillBandwidth(sendBWEstimateAvg * 1000);
      // }
      if(false === this.test.bitrate_only) {
        this.test.reportInfo(`Packets sent: ${this.packetsSent}`);
        this.test.reportInfo(`Packets received: ${this.packetsReceived}`);
      }
      let pktLoss = null;
      let pktLostPercentage = null;
      if(this.packetsSent > 0) {
        pktLostPercentage = ((this.packetsSent - this.packetsReceived) / this.packetsSent) * 100;
    
        if (pktLostPercentage < PACKET_LOST_RANGE[0]) {
          pktLoss = GOOD;
        } else if (pktLostPercentage < PACKET_LOST_RANGE[1]) {
          pktLoss = MODERATE;
        } else {
          pktLoss = POOR;
        }
    
        this.test.reportByState(`Packets lost: ${pktLostPercentage.toFixed(2)}%`, pktLoss);
        if(true === this.test.bitrate_only) {
          this.test.fillPacketsLost(pktLostPercentage.toFixed(2));
        }
      }
      let jit = this.jitter * 1000;
      let jitter = null;
      if(jit > JITTER_RANGE[1] || jit <= JITTER_RANGE[0]) {
        jitter = POOR;
      } else {
        jitter = GOOD;
        this.test.reportByState(`Jitter: ${jit}`, jitter);
      }
      let bandwidthSend = ((this.audioBytesSent * 8) / (durationS * 1000)).toFixed(2);
      let bandwidthReceived = ((this.audioBytesRecieved * 8) / (durationS * 1000)).toFixed(2);

      this.test.reportInfo(`Audio Bitrate (Send) :${bandwidthSend} kbps`);
      this.test.reportInfo(`Audio Bitrate (Receive) :'${bandwidthReceived} 'kbps`);
    }
    let rttAverage = this.rttStats.getAverage();
    let rttValue = null;
    let pktLoss = null;
    if (rttAverage < RTT_RANGE[0]) {
      rttValue = GOOD;
    } else if (rttAverage < RTT_RANGE[1]) {
      rttValue = MODERATE;
    } else {
      rttValue = POOR;
    }

    this.test.reportByState(`RTT average: ${rttAverage} ms`, rttValue);
    if(false === this.test.bitrate_only) {
      this.test.reportInfo(`RTT max: ${this.rttStats.getMax()} ms`);
    }
    this.test.done();
  },
};

export function testThroughPut(test, deviceId, testDuration) {
  switch (test.test_title) {
    case 'data_throughput': {
      // Creates a loopback via relay candidates and tries to send as many packets
      // with 1024 chars as possible while keeping dataChannel bufferedAmmount above
      // zero.
      const dataChannelThroughputTest = new DataChannelThroughputTest(test, testDuration);
      dataChannelThroughputTest.run();
    }
    break;
    case 'video_bandwidth': {
      // Measures video bandwidth estimation performance by doing a loopback call via
      // relay candidates for 40 seconds. Computes rtt and bandwidth estimation
      // average and maximum as well as time to ramp up (defined as reaching 75% of
      // the max bitrate. It reports infinite time to ramp up if never reaches it.
      const videoBandwidthTest = new VideoBandwidthTest(test, deviceId, testDuration);
      videoBandwidthTest.run();
      break;
    }
    case 'audio_bandwidth': {
      // Measures audio bandwidth estimation performance by doing a loopback call via
      // relay candidates for 10 seconds. Computes rtt and bandwidth estimation
      // average and maximum as well as time to ramp up (defined as reaching 75% of
      // the max bitrate. It reports infinite time to ramp up if never reaches it.
      const audioBandwidthTest = new AudioBandwidthTest(test, deviceId, testDuration);
      audioBandwidthTest.run();
      break;
    }
    case 'network_latency': {
      const wiFiPeriodicScanTest = new WiFiPeriodicScanTest(test, Call.isNotHostCandidate);
      wiFiPeriodicScanTest.run();
      break;
    }
    // case 'network_latency_relay': {
    //   const wiFiPeriodicScanTest = new WiFiPeriodicScanTest(test, Call.isRelay);
    //   wiFiPeriodicScanTest.run();
    //   break;
    // }
  }
}



//////////////////
// WEBPACK FOOTER
// ./src/testWebRtc/bandwidth_test.js
// module id = 211
// module chunks = 0